import type { ModuleKey } from '@hrms/shared';

/**
 * Which part of the web app belongs to which module, as route prefixes. The route guard uses it so a
 * direct link (a bookmark, a link in an email) to a switched-off module shows "not turned on" instead of
 * a half-working page full of refused requests.
 *
 * One table, checked against the menu by a test: every link in a section tagged with a module must fall
 * under one of that module's prefixes, so a new page cannot quietly escape the guard. Pages that are not
 * in the menu (a ticket's detail page, a trip's detail page) are covered by the prefix of their section.
 *
 * Longest prefix wins, which is how `/engage/recognition` belongs to Recognition while the rest of
 * `/engage` belongs to Engage. `core_hr` and `multi_entity` have no routes of their
 * own here: core HR is always on, and multiple legal entities is a capability inside Organisation.
 */
export const MODULE_ROUTE_ROOTS: Readonly<Partial<Record<ModuleKey, readonly string[]>>> = {
  helpdesk: ['/helpdesk', '/admin/helpdesk'],
  // `/learning` is the learner side since the training redesign; `/training` keeps old links guarded
  // on their way to it.
  training: ['/learning', '/training', '/admin/training'],
  engage: ['/engage', '/admin/engage'],
  offboarding: ['/exit', '/team/exits', '/admin/offboarding', '/admin/employee/resignations'],
  travel: ['/travel', '/admin/travel'],
  statutory_payroll: ['/admin/payroll', '/payslip', '/loans'],
  attendance: ['/attendance'],
  leave: ['/leave', '/admin/time/leave-year-end', '/admin/time/leave-plans', '/admin/time/leave-reports'],
  projects: ['/projects', '/timesheet'],
  onboarding: ['/my-onboarding', '/admin/onboarding'],
  asset_management: ['/asset-management'],
  recognition: ['/engage/recognition', '/admin/performance/recognition'],
  // Shift pages live under /attendance; the longest prefix keeps them separate from Attendance itself.
  shift_rostering: ['/attendance/shifts', '/attendance/shift-roster'],
  chat: ['/chat', '/admin/chat'],
  ai_copilot: ['/admin/ai'],
  performance_okr: ['/performance', '/admin/performance'],
  ats_recruitment: ['/admin/recruitment', '/my-referrals', '/jobs'],
  expense_reimbursement: ['/claims', '/admin/reimbursement', '/admin/expenses'],
  custom_workflow: ['/admin/settings/approval-workflows'],
  // Connected applications (ADR 0033): the Applications pages; the Calendars tab is not part of it.
  app_integrations: ['/admin/settings/integrations/applications'],
};

const ENTRIES = Object.entries(MODULE_ROUTE_ROOTS).flatMap(([module, roots]) =>
  (roots ?? []).map((root) => ({ module: module as ModuleKey, root })),
);

/**
 * Is this module on? A session that carries no module list at all (an older response, a test fixture)
 * counts as "everything on", the same way the menu treats it: unknown must never hide a page.
 */
export function isModuleOn(
  enabledModules: readonly ModuleKey[] | undefined,
  module: ModuleKey,
): boolean {
  return enabledModules === undefined || enabledModules.includes(module);
}

function underRoot(pathname: string, root: string): boolean {
  return pathname === root || pathname.startsWith(`${root}/`);
}

/** The module a page belongs to, or null for a page that belongs to none (always available). */
export function moduleForPath(pathname: string): ModuleKey | null {
  let best: { module: ModuleKey; length: number } | null = null;
  for (const { module, root } of ENTRIES) {
    if (underRoot(pathname, root) && (best === null || root.length > best.length)) {
      best = { module, length: root.length };
    }
  }
  return best?.module ?? null;
}

/**
 * The module that is switched off for this page, or null when the page may open. A page that belongs to
 * no module is never blocked, and neither is Settings > Modules itself (the only way back on).
 */
export function switchedOffModuleFor(
  pathname: string,
  enabledModules: readonly ModuleKey[] | undefined,
): ModuleKey | null {
  const module = moduleForPath(pathname);
  if (module === null) return null;
  return isModuleOn(enabledModules, module) ? null : module;
}
